import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { Box, CircularProgress, IconButton } from "@mui/material";
import { useEffect, useState } from "react";
import { toast } from "react-toastify";

import Upload from "@/Components/Icons/Upload";
import { VisuallyHiddenInput } from "@/Components/v2/atoms/VisuallyHiddenInput";

import { useListDocuments, useUploadDocument } from "../api";
import { Document } from "../api/schema/openapi";
import { useChat } from "../hooks/useChat";
import DeleteConfirmation from "./DeleteConfirmation";
import { DocumentsSelect } from "./DocumentsSelect";

type DocumentUploadProps = {
  hasPendingAction: boolean;
};

type DeleteConfirmation =
  | {
      isOpen: false;
    }
  | {
      isOpen: true;
      document: Document;
    };

export const DocumentUpload = ({ hasPendingAction }: DocumentUploadProps) => {
  const [showSuccess, setShowSuccess] = useState(false);
  const uploadDocument = useUploadDocument();
  const referenceDocuments = useListDocuments();
  const { enabledDocumentIds, setEnabledDocumentIds } = useChat();
  const [deleteConfirmation, setDeleteConfirmation] = useState<DeleteConfirmation>({
    isOpen: false,
  });

  const handleUploadDocument = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const MAX_FILE_SIZE = 10 * 1024 * 1024;
    if (file.size > MAX_FILE_SIZE) {
      toast.error("File size must be less than 10MB");
      event.target.value = "";
      return;
    }

    const response = await uploadDocument.mutateAsync({
      file: file,
      name: file.name,
    });

    toast.success(`${file.name || "File"} uploaded`);
    event.target.value = "";

    await referenceDocuments.refetch();
    setEnabledDocumentIds((prev) => [...new Set([...prev, response.data.id])]);
  };

  const handleFilterChange = (ids: string[]) => {
    setEnabledDocumentIds(ids);
  };

  const isDisabled = uploadDocument.isPending || showSuccess;

  useEffect(() => {
    let timeoutId: NodeJS.Timeout;

    if (!uploadDocument.isPending && showSuccess) {
      timeoutId = setTimeout(() => {
        setShowSuccess(false);
      }, 3000);
    }

    return () => {
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [uploadDocument.isPending, showSuccess]);

  useEffect(() => {
    if (uploadDocument.isSuccess) {
      setShowSuccess(true);
    }
  }, [uploadDocument.isSuccess]);

  const handleDocumentArchived = (documentId: string) => {
    const document = referenceDocuments.data?.find((doc) => doc.id === documentId);
    if (!document) return;

    setDeleteConfirmation({
      isOpen: true,
      document,
    });
  };

  return (
    <>
      <IconButton component="label" disabled={isDisabled} size="small" sx={{ mr: 1 }}>
        <VisuallyHiddenInput
          type="file"
          accept=".pdf"
          onChange={handleUploadDocument}
          disabled={isDisabled}
        />
        {uploadDocument.isPending ? (
          <CircularProgress size={24} />
        ) : showSuccess ? (
          <CheckCircleIcon color="success" />
        ) : (
          <Upload />
        )}
      </IconButton>

      <Box>
        <DocumentsSelect
          options={
            referenceDocuments.data?.map((doc) => ({
              label: doc.name,
              value: doc.id,
            })) ?? []
          }
          multiple
          value={enabledDocumentIds}
          onChange={handleFilterChange}
          label=""
          id="documents"
          disabled={hasPendingAction}
          placeholderOverride="Select Strategy Document(s)"
          width={300}
          onDocumentArchived={handleDocumentArchived}
        />
      </Box>

      {deleteConfirmation.isOpen && (
        <DeleteConfirmation
          handleClose={() => setDeleteConfirmation({ isOpen: false })}
          document={deleteConfirmation.document}
        />
      )}
    </>
  );
};
